import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  ProjectRestoreBucketJob,
  ProjectRestoreJob,
} from '../../../../../../typings/api/skymap/rest/v1/project';
import { InfoBox } from '../../info-box/info-box';
import { BucketsTable, BucketsTableItem } from './buckets-table';

interface Props {
  job: ProjectRestoreJob;
}

const RestoreInfoBox = (props: Props) => {
  const { t } = useTranslation();

  if (props.job.status === 'DONE') {
    throw new Error('RestoreInfoBox component cannot be rendered for status DONE.');
  }

  const resolveIconFromBucketJobState = (
    bucketJob: ProjectRestoreBucketJob,
  ): BucketsTableItem['state'] => {
    switch (bucketJob.state) {
      case 'CREATE_S3_COPY_BATCH_JOB':
      case 'CREATE_S3_RESTORE_BATCH_JOB':
      case 'WAIT_FOR_S3_COPY_BATCH_JOB':
      case 'WAIT_FOR_S3_RESTORE_BATCH_JOB':
      case 'WAIT_FOR_STATE_MACHINE_COMPLETE_RESTORE_FILES_OVER_5GB':
        return bucketJob.errorMessage
          ? {
              icon: {
                name: 'exclamation-circle',
                color: 'crimson',
                title: t('projectSettings.status.archiveStates.unexpectedError', {
                  ns: 'components',
                }),
              },
            }
          : {
              icon: {
                name: 'spinner',
                spin: true,
              },
            };

      case 'DONE':
        return t('projectSettings.status.restoreStates.done', { ns: 'components' });

      case 'IDLE':
        return t('projectSettings.status.archiveStates.notStarted', { ns: 'components' });
    }
  };

  const renderTitle = () => {
    switch (props.job.status) {
      case 'IDLE':
        return t('projectSettings.status.restoreStates.awaitingRestore', { ns: 'components' });
      case 'IN_PROGRESS':
        return t('projectSettings.status.restoreStates.restoreInProgress', { ns: 'components' });
      case 'ERROR':
        return t('projectSettings.status.archiveStates.checkS3Status', { ns: 'components' });
    }
  };

  const renderContent = () => {
    return (
      <>
        <span>{renderTitle()}</span>

        {props.job.bucketJobs.length > 0 && (
          <BucketsTable
            buckets={props.job.bucketJobs.map((x) => ({
              id: x.id,
              name: x.projectArchiveBucketJob.s3BucketName,
              state: resolveIconFromBucketJobState(x),
            }))}
          />
        )}
      </>
    );
  };

  return (
    <Component>
      <InfoBox bottomMargin={true} color="yellow" leftIcon={{ icon: ['fad', 'lock-alt'] }}>
        {renderContent()}
      </InfoBox>
    </Component>
  );
};

const Component = styled.div``;

export { RestoreInfoBox };
